/**
 * lbConfetti — Efeito de confete em cascata (chuva do topo)
 *
 * Partículas nascem aleatoriamente no topo da tela e caem com gravidade suave,
 * girando livremente. O canvas é criado sob demanda e removido após a animação.
 *
 * @example
 * lbConfetti.burst();
 * lbConfetti.burst({ particleCount: 120, colors: ['#009245', '#11426B'] });
 *
 * @package Labead
 */

const DEFAULT_COLORS = ['#6366f1', '#8b5cf6', '#fbbf24', '#10b981', '#ec4899', '#3b82f6', '#f97316', '#ef4444'];

function randomRange(min, max) {
	return Math.random() * (max - min) + min;
}

/** Play the confetti sound effect (labead-only asset, best-effort). */
function playConfettiSound() {
	if (!window.lb_data?.assets_url || window.lbSonsMudos?.()) return; // "Silenciar sons" (sons.js).
	try {
		const audio = new Audio(lb_data.assets_url + 'audio/confetti.mp3');
		audio.volume = 0.35;
		audio.play().catch(() => { });
	} catch (_) { /* no audio support */ }
}

function makeParticles(count, colors, width) {
	const particles = [];
	for (let i = 0; i < count; i++) {
		particles.push({
			x: randomRange(0, width),
			y: randomRange(-40, -4),
			vx: randomRange(-3.5, 3.5),
			vy: randomRange(2, 6),
			size: randomRange(5, 11),
			color: colors[i % colors.length],
			rotation: randomRange(0, 360),
			rotationSpeed: randomRange(-14, 14),
			shape: Math.random() > 0.4 ? 'rect' : 'circle',
			opacity: 1,
		});
	}
	return particles;
}

/**
 * Dispara uma chuva de confete caindo do topo da tela.
 *
 * @param {Object}   [opts]
 * @param {number}   [opts.particleCount=100] Quantidade de partículas.
 * @param {string[]} [opts.colors]            Paleta de cores (hex).
 * @param {number}   [opts.duration=4000]     Duração aproximada em ms.
 * @param {boolean}  [opts.sound=true]        Toca assets/audio/confetti.mp3 junto.
 * @return {void}
 */
function burst(opts = {}) {
	if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
		return;
	}

	if (opts.sound !== false) {
		playConfettiSound();
	}

	const particleCount = opts.particleCount || 100;
	const colors = opts.colors && opts.colors.length ? opts.colors : DEFAULT_COLORS;
	const duration = opts.duration || 4000;

	const canvas = document.createElement('canvas');
	canvas.style.position = 'fixed';
	canvas.style.inset = '0';
	canvas.style.width = '100vw';
	canvas.style.height = '100vh';
	canvas.style.pointerEvents = 'none';
	canvas.style.zIndex = '999999';
	document.body.appendChild(canvas);

	const dpr = window.devicePixelRatio || 1;
	const width = window.innerWidth;
	const height = window.innerHeight;
	canvas.width = width * dpr;
	canvas.height = height * dpr;
	const ctx = canvas.getContext('2d');
	ctx.scale(dpr, dpr);

	const particles = makeParticles(particleCount, colors, width);
	const gravity = 0.09;
	const drag = 0.997;
	const startTime = performance.now();
	let rafId = null;

	function cleanup() {
		if (rafId) cancelAnimationFrame(rafId);
		canvas.remove();
	}

	function tick(now) {
		const elapsed = now - startTime;
		ctx.clearRect(0, 0, width, height);

		let anyVisible = false;
		const fadeStart = duration * 0.65;

		particles.forEach((p) => {
			p.vy += gravity;
			p.vx *= drag;
			p.x += p.vx;
			p.y += p.vy;
			p.rotation += p.rotationSpeed;

			if (elapsed > fadeStart) {
				p.opacity = Math.max(0, 1 - (elapsed - fadeStart) / (duration - fadeStart));
			}

			if (p.opacity <= 0 || p.y > height + 20) {
				return;
			}
			anyVisible = true;

			ctx.save();
			ctx.globalAlpha = p.opacity;
			ctx.translate(p.x, p.y);
			ctx.rotate((p.rotation * Math.PI) / 180);
			ctx.fillStyle = p.color;
			if ('circle' === p.shape) {
				ctx.beginPath();
				ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
				ctx.fill();
			} else {
				ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
			}
			ctx.restore();
		});

		if (anyVisible && elapsed < duration) {
			rafId = requestAnimationFrame(tick);
		} else {
			cleanup();
		}
	}

	rafId = requestAnimationFrame(tick);
	setTimeout(cleanup, duration + 500);
}

const lbConfetti = { burst };

export default lbConfetti;

if (typeof window !== 'undefined') {
	window.lbConfetti = lbConfetti;
}
